import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getToken, setToken, removeToken, apiFetchJson } from '@/lib/api';
const AuthContext = createContext(null);
export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [usage, setUsage] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const fetchMe = useCallback(async () => {
        const token = getToken();
        if (!token) {
            setIsLoading(false);
            return;
        }
        try {
            const data = await apiFetchJson('/auth/me');
            if (data.success) {
                setUser(data.user);
                setUsage(data.usage);
            }
            else {
                removeToken();
                setUser(null);
            }
        }
        catch {
            removeToken();
            setUser(null);
        }
        finally {
            setIsLoading(false);
        }
    }, []);
    useEffect(() => {
        fetchMe();
    }, [fetchMe]);
    // Listen for 401 events from apiFetch
    useEffect(() => {
        const handler = () => {
            setUser(null);
            setUsage(null);
        };
        window.addEventListener('owl:auth-expired', handler);
        return () => window.removeEventListener('owl:auth-expired', handler);
    }, []);
    const login = useCallback(async (email) => {
        return apiFetchJson('/auth/magic-code', {
            method: 'POST',
            body: JSON.stringify({ email }),
        });
    }, []);
    const verify = useCallback(async (email, code) => {
        const data = await apiFetchJson('/auth/verify', {
            method: 'POST',
            body: JSON.stringify({ email, code }),
        });
        if (data.success && data.token) {
            setToken(data.token);
            setUser(data.user);
            // Fetch usage after login
            try {
                const meData = await apiFetchJson('/auth/me');
                if (meData.success)
                    setUsage(meData.usage);
            }
            catch {
                // ignore
            }
        }
        return data;
    }, []);
    const logout = useCallback(() => {
        removeToken();
        setUser(null);
        setUsage(null);
    }, []);
    const refreshUsage = useCallback(async () => {
        try {
            const data = await apiFetchJson('/auth/me');
            if (data.success) {
                setUsage(data.usage);
            }
        }
        catch {
            // ignore
        }
    }, []);
    return (_jsx(AuthContext.Provider, { value: { user, usage, isLoading, login, verify, logout, refreshUsage }, children: children }));
}
export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx)
        throw new Error('useAuth must be used within AuthProvider');
    return ctx;
}
//# sourceMappingURL=AuthContext.js.map